import { useMemo } from "react";
import { Link, Outlet, useNavigate } from "react-router";
import {
  ArrowRight,
  Sparkles,
  Coins,
  Lock,
  Download,
  LayoutGrid,
  Crown,
  Check,
  MapPin,
  Building2,
  Terminal,
  Wand2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";

// ─────────────────────────────────────────────────────────
// Home — Activation & education (Trendtrack-inspired)
// ─────────────────────────────────────────────────────────

type PlanTier = "free" | "pro";

export default function NewHomePage() {
  const navigate = useNavigate();
  const { sm } = useDetectSizeDevice();

  // Plan detection — no plan table yet, default Free.
  const plan: PlanTier = "free";

  const focusSearch = () => document.getElementById("home-search")?.focus();

  return (
    <>
      <Outlet />
      <div
        style={{
          height: sm
            ? "calc(100dvh - var(--height-header))"
            : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
        }}
        className="mx-auto w-full overflow-y-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-10 sm:space-y-14"
      >
        {/* 1. Hero — AI search */}
        <HeroSearch />

        {/* 2. MCP education block */}
        <MCPBlock />

        {/* 3 + 4 — Credits & Plan side by side on desktop */}

        {/* 6. Ce que vous pouvez faire */}
        <CapabilitiesGrid
          plan={plan}
          onUpgrade={() => navigate("/upgrade")}
          onMCP={() => navigate("/connectors")}
        />

        {/* 7. Actions recommandées */}
        <RecommendedActions
          onZone={() => {
            setTimeout(focusSearch, 0);
          }}
          onBrand={() => {
            setTimeout(focusSearch, 0);
          }}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Hero search
// ─────────────────────────────────────────────────────────

function HeroSearch() {
  return (
    <section className="text-center space-y-6 sm:space-y-8">
      <div className="space-y-3 sm:space-y-4 max-w-3xl mx-auto">
        <Badge variant="outline" className="mx-auto gap-1.5 px-2.5 py-1">
          <Sparkles className="h-3 w-3 text-primary" />
          <span>Krunsh Search</span>
        </Badge>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
          Que voulez-vous analyser aujourd'hui&nbsp;?
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Recherchez une zone ou une enseigne pour explorer le marché de la
          livraison sur Uber Eats et Deliveroo.
        </p>
        <Link to="/explorer">
          <Button size="sm" className="h-10 gap-1.5 rounded-xl">
            Aller sur Explorer
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────
// MCP block
// ─────────────────────────────────────────────────────────

function MCPBlock() {
  return (
    <section>
      <Card
        role="link"
        tabIndex={0}
        className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/[0.04] via-card to-card cursor-pointer hover:border-primary/30 transition-colors"
      >
        <CardContent className="p-5 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            {/* Left — content */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant="outline"
                  className="gap-1 border-primary/30 text-primary"
                >
                  <Terminal className="h-3 w-3" />
                  Krunsh IA
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <Coins className="h-3 w-3" />
                  Crédit IA
                </Badge>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
                  Besoin d'une analyse plus avancée&nbsp;?
                </h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Connectez Krunsh MCP à Claude ou ChatGPT pour poser des
                  questions complexes, comparer des enseignes et automatiser vos
                  analyses.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Benchmark", "Comparaison", "Opportunités"].map((cap) => (
                  <Badge key={cap} variant="outline" className="bg-card">
                    {cap}
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Link to="/connectors">
                  <Button className="gap-1.5">
                    <Wand2 className="h-4 w-4" />
                    Configurer Krunsh IA
                  </Button>
                </Link>
                <Link to="/connectors">
                  <Button variant="primaryFilled" className="gap-1.5">
                    Voir les guides
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right — mini MCP UI */}
            <MCPMiniPreview />
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function MCPMiniPreview() {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
            <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
            <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground ml-1">
            Krunsh MCP
          </span>
        </div>
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 h-4 border-primary/30 text-primary"
        >
          live
        </Badge>
      </div>
      <div className="p-4 space-y-3">
        <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-1">
            Prompt
          </div>
          <div className="text-[13px] text-foreground font-medium leading-snug">
            Compare KFC, Burger King et O'Tacos à Lyon
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            Tools
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["search-restaurants", "createGrid", "get-restaurant-details"].map(
              (t) => (
                <code
                  key={t}
                  className="text-[11px] font-mono rounded border border-border bg-muted/40 px-1.5 py-0.5 text-foreground/80"
                >
                  {t}
                </code>
              ),
            )}
          </div>
        </div>
        <div className="rounded-lg border border-primary/20 bg-primary/[0.04] p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
            <Check className="h-3.5 w-3.5 text-primary" />
            Benchmark prêt
          </div>
          <div className="text-[12px] text-muted-foreground pl-5">
            3 enseignes comparées · 2 opportunités détectées
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Capabilities grid
// ─────────────────────────────────────────────────────────

type CapStatus = "included" | "kc" | "kc_pro" | "pro";

function CapabilitiesGrid({
  plan,
  onUpgrade,
  onMCP,
}: {
  plan: PlanTier;
  onUpgrade: () => void;
  onMCP: () => void;
}) {
  const items: Array<{
    icon: React.ElementType;
    title: string;
    text: string;
    status: CapStatus;
    onClick?: () => void;
    ctaLabel?: string;
  }> = [
    {
      icon: MapPin,
      title: "Recherche par zone",
      text: "Analysez une ville, une adresse ou une zone pour comprendre le marché local.",
      status: "included",
    },
    {
      icon: Building2,
      title: "Recherche par enseigne",
      text: "Retrouvez une enseigne ou un restaurant et consultez ses données publiques.",
      status: "included",
    },
    {
      icon: Terminal,
      title: "Krunsh IA",
      text: "Connectez Claude ou ChatGPT et posez des questions complexes via Krunsh IA.",
      status: "kc_pro",
      onClick: onMCP,
      ctaLabel: "Découvrir le MCP",
    },
    {
      icon: Download,
      title: "Export",
      text: "Transformez vos recherches en données exploitables.",
      status: "kc",
    },
    {
      icon: LayoutGrid,
      title: "Grid Builder",
      text: "Comparez plusieurs enseignes dans une grille structurée.",
      status: "pro",
    },
  ];

  return (
    <section className="space-y-4">
      <SectionHeader
        title="Ce que Krunsh vous permet de faire"
        subtitle="Des capacités concrètes, activables selon votre plan et vos KC."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((it) => (
          <CapabilityCard
            key={it.title}
            {...it}
            plan={plan}
            onUpgrade={onUpgrade}
          />
        ))}
      </div>
    </section>
  );
}

function CapabilityCard({
  icon: Icon,
  title,
  text,
  status,
  plan,
  onUpgrade,
  onClick,
  ctaLabel,
}: {
  icon: React.ElementType;
  title: string;
  text: string;
  status: CapStatus;
  plan: PlanTier;
  onUpgrade: () => void;
  onClick?: () => void;
  ctaLabel?: string;
}) {
  const isProLocked = status === "pro" && plan === "free";

  const badge = useMemo(() => {
    switch (status) {
      case "included":
        return <Badge>Inclus</Badge>;
      case "kc":
        return (
          <Badge variant="secondary" className="gap-1">
            <Coins className="h-3 w-3" />
            Via KC
          </Badge>
        );
      case "kc_pro":
        return (
          <Badge variant="secondary" className="gap-1">
            <Coins className="h-3 w-3" />
            KC / Pro
          </Badge>
        );
      case "pro":
        return (
          <Badge
            variant="outline"
            className="gap-1 border-primary/30 text-primary"
          >
            <Crown className="h-3 w-3" />
            Disponible avec Pro
          </Badge>
        );
    }
  }, [status]);

  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative h-full transition-colors p-0",
        isProLocked && "bg-muted/20",
        onClick && "cursor-pointer hover:border-primary/30",
      )}
    >
      <CardContent className="p-5 space-y-3 h-full flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div
            className={cn(
              "h-9 w-9 rounded-lg flex items-center justify-center",
              isProLocked
                ? "bg-muted text-muted-foreground"
                : "bg-primary/10 text-primary",
            )}
          >
            <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
          </div>
          {isProLocked && (
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1">
          <div className="text-[14px] font-semibold text-foreground">
            {title}
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {text}
          </p>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          {badge}
          {isProLocked ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpgrade();
              }}
              className="text-[11px] font-medium text-primary hover:underline"
            >
              Débloquer
            </button>
          ) : onClick && ctaLabel ? (
            <span className="text-[11px] font-medium text-primary inline-flex items-center gap-1">
              {ctaLabel}
              <ArrowRight className="h-3 w-3" />
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// Recommended actions
// ─────────────────────────────────────────────────────────

function RecommendedActions({
  onZone,
  onBrand,
}: {
  onZone: () => void;
  onBrand: () => void;
}) {
  const navigate = useNavigate();

  const actions = [
    {
      icon: MapPin,
      title: "Analyser une zone",
      text: "Commencez par une ville, une adresse ou un quartier.",
      cta: "Rechercher une zone",
      onClick: onZone,
      badge: null,
    },
    {
      icon: Building2,
      title: "Rechercher une enseigne",
      text: "Trouvez une marque ou un restaurant spécifique.",
      cta: "Rechercher une enseigne",
      onClick: onBrand,
      badge: null,
    },
    {
      icon: Terminal,
      title: "Tester Krunsh IA",
      text: "Posez une question avancée et utilisez vos KC pour obtenir une réponse structurée.",
      cta: "Découvrir Krunsh IA",
      onClick: () => navigate("/connectors"),
      badge: "MCP" as const,
    },
  ];

  return (
    <section className="space-y-4">
      <SectionHeader
        title="Actions recommandées"
        subtitle="Un pas de plus vers votre première analyse."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        {actions.map((a) => (
          <Card
            key={a.title}
            className="group hover:border-primary/30 transition-colors p-0"
          >
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <a.icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[14px] font-semibold text-foreground truncate">
                      {a.title}
                    </div>
                    {a.badge && (
                      <Badge
                        variant="outline"
                        className="border-primary/30 text-primary text-[10px] px-1.5 py-0"
                      >
                        {a.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {a.text}
                  </p>
                  <Button
                    variant="secondaryFilled"
                    size="sm"
                    className="-ml-2 mt-2 h-7 text-primary hover:text-primary hover:bg-primary/5"
                    onClick={a.onClick}
                  >
                    {a.cta}
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────
// Shared section header
// ─────────────────────────────────────────────────────────

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="space-y-1">
      <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-foreground">
        {title}
      </h2>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
